import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import GuidedJournal from './components/GuidedJournal';
import Archive from './archive/Archive';
import { StormProvider } from './storm/StormContext';
import Storm from './storm/Storm';
import StormCalibrator from './storm/StormCalibrator';
import { ChaosProvider } from './chaos/ChaosContext';
import TearTransition from './chaos/TearTransition';
import { pointerEngine } from './play/pointerEngine';
import EffectsCanvas from './play/EffectsCanvas';
import Reticle from './play/Reticle';
import { PlayLink } from './play/PlayTarget';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Layout component that conditionally renders header and footer
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [showGlitch, setShowGlitch] = useState(false);

  // Exclude header/footer from home page
  const shouldHideNav = location.pathname === '/';
  
  // Home screen style animation for the logo
  useEffect(() => {
    if (isLogoHovered && (location.pathname === '/journal' || location.pathname === '/write')) {
      // Create dynamic title style cycling like home screen
      const styleInterval = setInterval(() => {
        // Pick a random letter to highlight instead of cycling sequentially
        // Only cycle through "ania" (4 letters), not "mania" (5 letters)
        const randomIndex = Math.floor(Math.random() * 4);
        setHighlightIndex(randomIndex);
      }, 200); // Fast random cycling
      
      const glitchInterval = setInterval(() => {
        // Random glitch effect
        if (Math.random() > 0.7) {
          setShowGlitch(true);
          setTimeout(() => setShowGlitch(false), 150);
        }
      }, 1200);
      
      return () => {
        clearInterval(styleInterval);
        clearInterval(glitchInterval);
      };
    } else {
      // Reset when not hovered
      setHighlightIndex(0);
      setShowGlitch(false);
    }
  }, [isLogoHovered, location.pathname]);
  

  
  // Render the logo with home screen style animation
  const renderLogo = () => {
    const isJournalPage = location.pathname === '/journal' || location.pathname === '/write';
    
    if (isJournalPage) {
      return (
        <div 
          className="flex items-center"
          onMouseEnter={() => setIsLogoHovered(true)}
          onMouseLeave={() => setIsLogoHovered(false)}
          style={{ minWidth: "200px" }}
        >
          {isLogoHovered ? (
            <span 
              className="text-flicker"
              style={{ 
                filter: showGlitch ? 'hue-rotate(90deg) brightness(1.5)' : 'none',
                transition: 'filter 0.1s'
              }}
            >
              {renderTitle()}
            </span>
          ) : (
            <span className="logo-m">m</span>
          )}
        </div>
      );
    } else {
      return <span className="logo-m">m</span>;
    }
  };
  
  // Render the title with one highlighted letter at a time (like home screen)
  const renderTitle = () => {
    const word = "ania";
    
    return (
      <span className="title-container">
        <span className="logo-m">m</span>
        {word.split('').map((letter, index) => (
          <span 
            key={`letter-${index}-${highlightIndex}`}
            className={index === highlightIndex 
              ? "letter-highlight" 
              : "letter-normal"}
          >
            {letter}
          </span>
        ))}
      </span>
    );
  };
  
  return (
    <>
      {/* Only show header and mobile menu on pages that aren't home */}
      {!shouldHideNav && (
        <>
          {/* Navigation - Hidden on mobile */}
          <nav className="relative z-50 py-1.5 border-b border-white/20 bg-black/95 backdrop-blur-md hidden md:block">
            <div className="w-full px-4 lg:px-6">
              <div className="flex justify-between items-center h-9">
                <div className="flex items-center">
                  <Link to="/" className="flex-shrink-0 flex items-center">
                    <div className="text-2xl md:text-3xl text-white font-light">
                      {renderLogo()}
                    </div>
                  </Link>
                </div>

                {/* Desktop menu */}
                <div className="hidden md:flex items-center gap-6">
                </div>

                <div className="hidden md:flex items-center gap-2">
                  <PlayLink to="/journal" className="text-white/70 hover:text-white text-sm">library</PlayLink>
                </div>
              </div>
            </div>
          </nav>

          {/* Phones: the nav above is hidden, so keep a small way into the library. */}
          {location.pathname === '/write' && (
            <PlayLink to="/journal" className="md:hidden"
              style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 12px)', right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
                zIndex: 50, color: '#f2f0e9', fontFamily: 'var(--font-ui)', fontSize: 14, padding: '6px 4px', textDecoration: 'none' }}>
              library
            </PlayLink>
          )}
        </>
      )}
      
       {/* Main content */}
       <main className="flex-1 relative z-10 overflow-visible">
        {children}
      </main>
      
      {/* Footer removed */}
    </>
  );
};

// AppContent component to use useLocation inside Router
const AppContent: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    pointerEngine.start();
    return () => pointerEngine.stop();
  }, []);

  return (
    <>
      <div
        className="min-h-dvh w-full flex flex-col overflow-x-clip"
        data-debug-component="appcontent-wrapper"
        style={{
          boxSizing: 'border-box',
          position: 'relative'
        }}
      >
        {/* The living storm — ambient layer above the routes so it persists across
            navigation (no route-swap flash) and drives intensity from one context. */}
        <Storm />
        {/* z1, before the routes: later z1+ content paints over the rings. */}
        <EffectsCanvas />
        <Reticle />

        {/* Route swaps are covered by the tear overlay (TearTransition), not a fade. */}
        <Routes location={location} key={location.pathname}>
            {/* Home retired — the /write intro IS the landing animation. Root lands there. */}
            <Route path="/" element={<Navigate to="/write" replace />} />
            <Route path="/journal" element={<Archive />} />
            <Route path="/write" element={<Layout key="guided-layout"><GuidedJournal key="guided-journal" /></Layout>} />
          </Routes>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <StormCalibrator />
      <TearTransition />
    </>
  );
};

function App() {
  return (
    <Router>
      <StormProvider>
        <ChaosProvider>
          <AppContent />
        </ChaosProvider>
      </StormProvider>
    </Router>
  );
}

export default App;
